import { useEffect, useRef, useState } from 'react';
import { FileText, Upload, Loader2, Check, ExternalLink } from 'lucide-react';
import { uploadResume, getResumeStatus, openResume } from '@/lib/api/auth';
import { useAuthStore } from '@/stores/authStore';

const MAX = 8 * 1024 * 1024;

export function ResumeUpload() {
  const uid = useAuthStore((s) => s.user?.id);
  const [hasResume, setHasResume] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { getResumeStatus().then((r) => setHasResume(r.has_resume)).catch(() => {}); }, []);

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr(null);
    if (file.type !== 'application/pdf') { setErr('PDF only.'); return; }
    if (file.size > MAX) { setErr('Resume must be at most 8 MB.'); return; }
    setBusy(true);
    try { await uploadResume(file); setHasResume(true); } catch { setErr('Upload failed. Try again.'); } finally { setBusy(false); }
  };

  const view = async () => {
    if (!uid) return;
    try { window.open(await openResume(uid), '_blank', 'noopener,noreferrer'); } catch { /* none */ }
  };

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
          <FileText className="h-5 w-5 text-muted-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Resume</p>
          <p className="text-xs text-muted-foreground">
            {hasResume ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" /> Uploaded — visible to your placement team</span> : 'PDF, up to 8 MB. Shared with your placement team.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasResume && (
            <button type="button" onClick={view} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted">
              <ExternalLink className="h-3.5 w-3.5" /> View
            </button>
          )}
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {hasResume ? 'Replace' : 'Upload'}
          </button>
        </div>
      </div>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={pick} />
      {err && <p className="mt-2 text-xs text-destructive">{err}</p>}
    </div>
  );
}
